import { ListFilter } from "lucide-react";
import { useTranslation } from "react-i18next";
import { tagColorClass } from "@/components/kanban/KanbanCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { TaskAssigneeFilter } from "@/hooks/use-kanban-tasks";
import type { Tag } from "@/lib/project-types";
import type { TaskDueFilter } from "@/lib/task-due-date";
import { cn } from "@/lib/utils";

interface TaskFilterMenuProps {
  assignee: TaskAssigneeFilter;
  onAssigneeChange: (value: TaskAssigneeFilter) => void;
  due: TaskDueFilter;
  onDueChange: (value: TaskDueFilter) => void;
  dueSupported: boolean | null;
  tagIds: string[];
  onTagIdsChange: (value: string[]) => void;
  tagSupported: boolean | null;
  tags: Tag[];
  hasCurrentUser: boolean;
  onAssigneePickerOpen?: () => void;
}

export function TaskFilterMenu({ assignee, onAssigneeChange, due, onDueChange, dueSupported,
  tagIds, onTagIdsChange, tagSupported, tags, hasCurrentUser, onAssigneePickerOpen }: TaskFilterMenuProps) {
  const { t } = useTranslation("projects");
  const activeCount = Number(assignee !== "all") + Number(due !== "all") + tagIds.length;
  const availableTags = tags.filter((tag) => !tag.is_archived);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" size="icon" variant={activeCount ? "secondary" : "ghost"}
          className="relative size-8 shrink-0" aria-label={t("filterButton")}
          aria-haspopup="dialog">
          <ListFilter className="size-4" />
          {activeCount > 0 && <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground" aria-hidden="true">{activeCount}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-0">
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">{t("filterPanelTitle")}</span>
          <Button type="button" size="sm" variant="ghost" className="h-7 px-2 text-xs"
            disabled={!activeCount} onClick={() => { onAssigneeChange("all"); onDueChange("all"); onTagIdsChange([]); }}>
            {t("filterClearAll")}
          </Button>
        </div>
        <div className="space-y-3 p-3">
          <div className="grid grid-cols-[5.5rem_1fr] items-center gap-2">
            <span className="text-xs text-muted-foreground">{t("filterAssignee")}</span>
            <Select value={assignee} onValueChange={(value) => onAssigneeChange(value as TaskAssigneeFilter)} onOpenChange={(open) => { if (open) onAssigneePickerOpen?.(); }}>
              <SelectTrigger className="h-8 text-xs" aria-label={t("filterAssignee")}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("filterAllTasks")}</SelectItem>
                <SelectItem value="mine" disabled={!hasCurrentUser}>{t("filterMyTasks")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {dueSupported !== null && <div className="grid grid-cols-[5.5rem_1fr] items-center gap-2">
            <span className="text-xs text-muted-foreground">{t("dueDateFilter")}</span>
            {dueSupported ? (
              <Select value={due} onValueChange={(value) => onDueChange(value as TaskDueFilter)}>
                <SelectTrigger className="h-8 text-xs" aria-label={t("dueDateFilter")}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("filterAllDates")}</SelectItem>
                  <SelectItem value="overdue">{t("filterOverdue")}</SelectItem>
                  <SelectItem value="upcoming">{t("filterUpcoming")}</SelectItem>
                  <SelectItem value="no_date">{t("filterNoDate")}</SelectItem>
                </SelectContent>
              </Select>
            ) : <span className="text-xs text-muted-foreground" role="status">{t("dueDatesUnavailable")}</span>}
          </div>}
          <div className="grid grid-cols-[5.5rem_1fr] items-start gap-2">
            <span className="pt-1 text-xs text-muted-foreground">{t("filterTag")}</span>
            {tagSupported === true ? (
              <div className="max-h-36 space-y-1 overflow-y-auto">
                {availableTags.length ? (<>
                  {tagIds.length === 0 && <span className="block px-1 py-1 text-xs text-muted-foreground">{t("filterNoneSelected")}</span>}
                  {availableTags.map((tag) => (
                    <label key={tag.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-accent">
                      <Checkbox checked={tagIds.includes(tag.id)} onChange={(event) => onTagIdsChange(event.target.checked ? [...tagIds, tag.id] : tagIds.filter((id) => id !== tag.id))} aria-label={tag.name} />
                      <Badge variant="outline" className={cn("max-w-40 truncate px-1.5 py-0 text-xs", tagColorClass(tag.color))}>{tag.name}</Badge>
                    </label>
                  ))}
                </>) : <span className="block px-1 py-1 text-xs text-muted-foreground" role="status">{t("noTagsYet")}</span>}
              </div>
            ) : <span className="text-xs text-muted-foreground" role="status">{tagSupported === false ? t("tagsUnavailable") : t("filterNoneSelected")}</span>}
          </div>
        </div>
        <div className="border-t p-1">
          <Button type="button" size="sm" variant="ghost" className="w-full justify-start text-xs"
            disabled={!activeCount} onClick={() => { onAssigneeChange("all"); onDueChange("all"); onTagIdsChange([]); }}>
            {t("filterClearAll")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
